import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Facebook, Inbox, UserCheck, UserX, AlertTriangle, Copy, RefreshCw, X,
    Megaphone, FileText, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
    fetchInboundLeadStats, fetchInboundLeadDetail, replayInboundLead,
} from '../api/analyticsApi';

/**
 * Facebook Leads — the enquiries coming in from Meta lead ads.
 *
 * Facebook and Instagram lead-ad forms post into the CRM through the lead
 * intake API (backend: lead_ingest). Every payload is stored the moment it
 * arrives, before anything is parsed, so this panel can show what came in, what
 * became a lead, who it went to, and anything that failed on the way.
 *
 * The most important number here is the smallest one: "last lead received". In
 * August 2026 Facebook silently revoked leads access and the feed stopped for
 * days with nobody noticing, because nothing anywhere showed that leads had
 * stopped arriving. That banner exists so it cannot happen twice.
 *
 * Test-mode payloads are filtered out by the backend, so everything on this
 * page is a real enquiry.
 */

const NUM = new Intl.NumberFormat('en-AU');
const fmt = (v) => (v == null ? '—' : NUM.format(v));

const STATUS_STYLES = {
    processed: { label: 'In the CRM', className: 'bg-green-50 text-green-700 border-green-200' },
    failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
    duplicate: { label: 'Duplicate', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    received: { label: 'Not processed', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    test: { label: 'Test', className: 'bg-blue-50 text-blue-700 border-blue-200' },
};

const StatusPill = ({ status }) => {
    const style = STATUS_STYLES[status] || STATUS_STYLES.received;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${style.className}`}>
            {style.label}
        </span>
    );
};

const StatCard = ({ Icon, label, value, sub, accent }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg"
                  style={{ background: `${accent}1a`, color: accent }}>
                <Icon className="w-4 h-4" />
            </span>
            <span className="text-xs font-medium text-gray-500">{label}</span>
        </div>
        <div className="text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
        {sub != null && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
);

const relativeDays = (iso) => {
    if (!iso) return null;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
};

const shortDateTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-AU', {
        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
};

// ── Is the feed alive? ───────────────────────────────────────────────────────
const FeedHealth = ({ lastReceivedAt }) => {
    const days = relativeDays(lastReceivedAt);

    if (lastReceivedAt == null) {
        return (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                    <p className="font-semibold text-amber-900">No leads received yet</p>
                    <p className="text-sm text-amber-800">
                        Nothing has arrived from Facebook. Either the Zaps are not pointed at the
                        CRM yet, or Facebook lead access is still switched off.
                    </p>
                </div>
            </div>
        );
    }

    // Three days is the judgement call: a quiet weekend is normal, a quiet
    // working week is the August outage happening again.
    const stale = days >= 3;
    return (
        <div className={`flex items-start gap-3 rounded-xl border p-4 ${
            stale ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
            {stale
                ? <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                : <Facebook className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />}
            <div>
                <p className={`font-semibold ${stale ? 'text-red-900' : 'text-green-900'}`}>
                    {stale
                        ? `No leads for ${days} days`
                        : 'Feed is live'}
                </p>
                <p className={`text-sm ${stale ? 'text-red-800' : 'text-green-800'}`}>
                    Last lead arrived {shortDateTime(lastReceivedAt)}.
                    {stale && ' Worth checking Facebook lead access and the Zaps before assuming it is a quiet week.'}
                </p>
            </div>
        </div>
    );
};

// ── Daily trend ──────────────────────────────────────────────────────────────
const TrendBars = ({ days }) => {
    const peak = useMemo(
        () => Math.max(1, ...days.map((d) => d.leads)),
        [days],
    );
    if (!days.length) return null;

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Leads per day</h3>
            <div className="flex items-end gap-0.5 h-28 overflow-x-auto">
                {days.map((d) => (
                    <div
                        key={d.date}
                        className="flex-1 min-w-[3px] bg-blue-500/80 hover:bg-blue-600 rounded-t transition-colors"
                        style={{ height: `${Math.max(2, (d.leads / peak) * 100)}%` }}
                        title={`${new Date(d.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}: ${d.leads} lead${d.leads === 1 ? '' : 's'}`}
                    />
                ))}
            </div>
            <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{days[0] && new Date(days[0].date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
                <span>{days.length > 1 && new Date(days[days.length - 1].date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
            </div>
        </div>
    );
};

// ── A ranked breakdown (campaign / form / agent) ─────────────────────────────
const RankedTable = ({ Icon, title, empty, rows, labelKey, accent }) => {
    const top = rows[0]?.leads || 1;
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4" style={{ color: accent }} />
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            </div>
            {rows.length === 0 ? (
                <p className="text-sm text-gray-400 py-3">{empty}</p>
            ) : (
                <div className="space-y-2">
                    {rows.map((row) => (
                        <div key={row[labelKey] || 'unknown'}>
                            <div className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-gray-700 truncate" title={row[labelKey]}>
                                    {row[labelKey] || 'Not recorded'}
                                </span>
                                <span className="font-semibold text-gray-900 tabular-nums shrink-0">
                                    {fmt(row.leads)}
                                </span>
                            </div>
                            <div className="h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                <div className="h-full rounded-full"
                                     style={{ width: `${(row.leads / top) * 100}%`, background: accent }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── What Facebook actually sent for one lead ─────────────────────────────────
const LeadDetailModal = ({ id, onClose, onReplayed }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replaying, setReplaying] = useState(false);

    useEffect(() => {
        let alive = true;
        fetchInboundLeadDetail(id)
            .then((res) => { if (alive) setData(res); })
            .catch(() => { if (alive) toast.error("Couldn't load this lead"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [id]);

    const replay = async () => {
        setReplaying(true);
        try {
            const res = await replayInboundLead(id);
            if (res.ok) {
                toast.success('Lead created in the CRM');
                onReplayed?.();
                onClose();
            } else {
                toast.error(res.error || 'Still failing, the cause has not been fixed yet');
            }
        } catch {
            toast.error("Couldn't replay this lead");
        } finally {
            setReplaying(false);
        }
    };

    const answers = Object.entries(data?.extra_fields || {});

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
             onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
                    <div>
                        <h3 className="font-bold text-gray-900">{data?.name || 'Lead'}</h3>
                        <p className="text-xs text-gray-500">Exactly what Facebook sent</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="p-8 text-center text-gray-400">Loading…</div>
                ) : !data ? (
                    <div className="p-8 text-center text-gray-400">Could not load this lead.</div>
                ) : (
                    <div className="p-5 space-y-5">
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                            {[
                                ['Email', data.email], ['Phone', data.phone],
                                ['Form', data.lead_form_name], ['Campaign', data.campaign_name],
                                ['Ad', data.ad_name], ['Ad set', data.adset_name],
                                ['Page', data.page_name], ['Platform', data.platform],
                                ['Submitted', shortDateTime(data.submitted_at)],
                                ['Agent', data.agent_name],
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <dt className="text-xs text-gray-500">{label}</dt>
                                    <dd className="text-gray-900 break-words">{value || '—'}</dd>
                                </div>
                            ))}
                        </dl>

                        {data.message && (
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 mb-1">Message</h4>
                                <p className="text-sm text-gray-800">{data.message}</p>
                            </div>
                        )}

                        {answers.length > 0 && (
                            <div>
                                <h4 className="text-xs font-semibold text-gray-500 mb-1">Form answers</h4>
                                <dl className="space-y-1 text-sm">
                                    {answers.map(([question, answer]) => (
                                        <div key={question} className="flex gap-2">
                                            {/* One template string, not `{question}:` — that
                                                splits the label across two text nodes. */}
                                            <dt className="text-gray-500 shrink-0">{`${question}:`}</dt>
                                            <dd className="text-gray-900">{String(answer)}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {data.error_message && (
                            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                                <p className="text-xs font-semibold text-red-800 mb-1">Why it failed</p>
                                <p className="text-sm text-red-700 break-words">{data.error_message}</p>
                                <button
                                    onClick={replay}
                                    disabled={replaying}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${replaying ? 'animate-spin' : ''}`} />
                                    {replaying ? 'Retrying…' : 'Try again'}
                                </button>
                            </div>
                        )}

                        <details className="rounded-lg border border-gray-200">
                            <summary className="px-3 py-2 text-xs font-semibold text-gray-600 cursor-pointer">
                                Raw payload
                            </summary>
                            <pre className="px-3 pb-3 text-xs text-gray-700 overflow-x-auto">
                                {JSON.stringify(data.raw_payload, null, 2)}
                            </pre>
                        </details>
                    </div>
                )}
            </div>
        </div>
    );
};

// ── The panel ────────────────────────────────────────────────────────────────
const FacebookLeadsPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openLeadId, setOpenLeadId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        return fetchInboundLeadStats(startDate, endDate)
            .then(setData)
            .catch(() => toast.error("Couldn't load Facebook leads"))
            .finally(() => setLoading(false));
    }, [startDate, endDate]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return <div className="py-16 text-center text-gray-400">Loading Facebook leads…</div>;
    }
    if (!data) {
        return <div className="py-16 text-center text-gray-400">No data available.</div>;
    }

    const recent = data.recent || [];

    return (
        <div className="space-y-4">
            <FeedHealth lastReceivedAt={data.last_lead_received_at} />

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <StatCard Icon={Inbox} label="Leads received" value={fmt(data.total)}
                          sub="In the selected period" accent="#2563eb" />
                <StatCard Icon={UserCheck} label="In the CRM" value={fmt(data.processed)}
                          sub="Contact and lead created" accent="#16a34a" />
                <StatCard Icon={UserX} label="Unassigned" value={fmt(data.unassigned)}
                          sub="Nobody owns these yet" accent="#d97706" />
                <StatCard Icon={AlertTriangle} label="Failed" value={fmt(data.failed)}
                          sub="Stored, can be retried" accent="#dc2626" />
                <StatCard Icon={Copy} label="Duplicates" value={fmt(data.duplicates)}
                          sub="Same lead sent twice" accent="#6b7280" />
            </div>

            <TrendBars days={data.by_day || []} />

            <div className="grid md:grid-cols-3 gap-3">
                <RankedTable
                    Icon={Megaphone} title="By campaign" accent="#2563eb"
                    rows={data.by_campaign || []} labelKey="campaign_name"
                    empty="No campaign names came through yet."
                />
                <RankedTable
                    Icon={FileText} title="By form" accent="#7c3aed"
                    rows={data.by_form || []} labelKey="lead_form_name"
                    empty="No form names came through yet."
                />
                <RankedTable
                    Icon={UserCheck} title="By agent" accent="#16a34a"
                    rows={data.by_agent || []} labelKey="agent"
                    empty="No leads assigned yet."
                />
            </div>

            <div className="bg-white rounded-xl border border-gray-200">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <h3 className="text-sm font-semibold text-gray-900">Latest leads</h3>
                    </div>
                    <button onClick={load}
                            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>

                {recent.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-gray-400">
                        No leads in this period.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                                    <th className="px-4 py-2 font-medium">Name</th>
                                    <th className="px-4 py-2 font-medium">Contact</th>
                                    <th className="px-4 py-2 font-medium">Form</th>
                                    <th className="px-4 py-2 font-medium">Campaign</th>
                                    <th className="px-4 py-2 font-medium">Agent</th>
                                    <th className="px-4 py-2 font-medium">Status</th>
                                    <th className="px-4 py-2 font-medium">Arrived</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.map((row) => (
                                    <tr key={row.id}
                                        onClick={() => setOpenLeadId(row.id)}
                                        className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer">
                                        <td className="px-4 py-2.5 font-medium text-gray-900">{row.name}</td>
                                        <td className="px-4 py-2.5 text-gray-600">
                                            <div className="truncate max-w-[14rem]">{row.email || '—'}</div>
                                            <div className="text-xs text-gray-400">{row.phone || ''}</div>
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-600 truncate max-w-[12rem]">
                                            {row.lead_form_name || '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-600 truncate max-w-[12rem]">
                                            {row.campaign_name || '—'}
                                        </td>
                                        <td className="px-4 py-2.5 text-gray-600">{row.agent_name || '—'}</td>
                                        <td className="px-4 py-2.5"><StatusPill status={row.status} /></td>
                                        <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                            {shortDateTime(row.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {openLeadId && (
                <LeadDetailModal
                    id={openLeadId}
                    onClose={() => setOpenLeadId(null)}
                    onReplayed={load}
                />
            )}
        </div>
    );
};

export default FacebookLeadsPanel;
